import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {DateInputProps} from '@Components/event-wizard/date-input/date-input.types';
import {ClickableDiv} from '@Components/clickable-div';
import {usePopover} from '@Components/popover/hooks/usePopover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {DateInputCalendarContent} from '@Components/event-wizard/date-input-calendar-content';
import {useDispatch} from 'react-redux';
import {onEventWizardDateTimeMobilePanelUpdate} from '@Components/event-wizard/event-wizard-slice';
import styles from './date-input.module.scss';

export function DateInput({
  selectedDate,
  minDate,
  maxDate,
  onSelectDate,
  className,
  isInErrorState = false,
  mobilePanelType = undefined,
  isDisabled = false,
}: DateInputProps): ReactElement<any> {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const {isPopoverOpen, togglePopover, closePopover, renderPopover} = usePopover({
    referenceElementRef: ref,
    alignment: PopoverDefaultAlignment.RIGHT_START,
    className: styles.popover,
    hasAutoPlacement: false
  });

  const toggleDatePickerVisibility = (): void => {
    if (mobilePanelType) {
      dispatch(onEventWizardDateTimeMobilePanelUpdate(mobilePanelType));
    } else {
      togglePopover();
    }
  };

  const onDateSelected = (date: Date): void => {
    if (onSelectDate) {
      onSelectDate(date);
    }
    closePopover();
  };

  const getFormattedDate = (): string => {
    const lang = 'en-US';

    const optionsWeekday: Intl.DateTimeFormatOptions = {
      weekday: 'short',
    };
    const optionsDate: Intl.DateTimeFormatOptions = {
      day: '2-digit',
    };

    const optionsMonth: Intl.DateTimeFormatOptions = {
      month: 'short',
    };

    const weekday = selectedDate.toLocaleDateString(lang, optionsWeekday);
    const dateNum = selectedDate.toLocaleDateString(lang, optionsDate);
    const month = selectedDate.toLocaleDateString(lang, optionsMonth);
    return `${weekday}, ${month} ${dateNum}`;
  };

  const getCalendarSelectorContent = (): ReactElement<any> => {
    return (
      <ClickableDiv
        className={`${styles.dateInputContent} ${isInErrorState ? styles.error : ''} ${isPopoverOpen ? styles.open : ''} ${isDisabled ? styles.disabled + ' bg-neutral-6' : ''} border-s-dark flex-row-align-center radius-8 _full-width`}
        onClick={!isDisabled ? toggleDatePickerVisibility : () => {}}
        ref={ref}
      >
        <Icon className="content-disabled spacing-m-r-2" icon="icon-calendar" size={IconSize.SIZE_ICON_16} />
        <Text className={isDisabled ? 'content-disabled' : 'content-body'} val={getFormattedDate()} size={TextSize.XSMALL} />
      </ClickableDiv>
    );
  };

  const getCalendarContent = (): ReactElement<any> => {
    return <DateInputCalendarContent minDate={minDate} maxDate={maxDate} selectedDate={selectedDate} onDateSelected={onDateSelected} />;
  };

  return (
    <div className={`${className}`}>
      {getCalendarSelectorContent()}
      {!mobilePanelType ? renderPopover(getCalendarContent()) : undefined}
    </div>
  );
}
